import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useComposedRefs } from "@tamagui/core";
import { TextInputBase } from "../shared/TextInputBase";
import {
  formatInputValue,
  getFormatProps,
  getSuggestion,
  useDebounce,
  validateInputValue
} from "./helpers";
import { jsx } from "react/jsx-runtime";
const ValidatedTextInput = forwardRef(function({
  onBlur,
  format,
  required = !1,
  prefilledValue,
  onChangeText,
  onValidationChange,
  allowSuggestions,
  /**
   * Passing errorText will immediately invoke an error - this is useful to trigger an error on things like async server errors
   * In most cases you will want ValidateTextInput to handle validation/error messsages for you
   * In cases where you want to customize your error message use customErrorMessages instead - the error message will then show based on the error type
   */
  errorText,
  customErrorMessages,
  getValue,
  ...props
}, ref) {
  const [validationState, setValidationState] = useState("unknown"), [currentValue, setCurrentValue] = useState(prefilledValue || ""), [suggestion, setSuggestion] = useState(), [errorMessage, setErrorMessage] = useState(errorText), inputRef = useRef(null), combinedInputRef = useComposedRefs(ref, inputRef), formatProps = useMemo(
    () => getFormatProps({ format, value: currentValue }),
    [format, currentValue]
  ), handleValidationChange = useCallback(
    (currentErrorMessage) => {
      setErrorMessage(currentErrorMessage);
      const newValidationState = currentErrorMessage ? "invalid" : "valid";
      setValidationState(newValidationState), onValidationChange && onValidationChange(newValidationState);
    },
    [onValidationChange]
  ), validateInput = useCallback(
    async (value) => {
      if (validateInputValue(handleValidationChange, value, format, required, customErrorMessages), allowSuggestions) {
        const suggestedValue = await getSuggestion(value, format);
        setSuggestion(suggestedValue);
      }
    },
    [format, required, allowSuggestions, customErrorMessages, handleValidationChange]
  ), debouncedValidateInput = useDebounce(validateInput, 1e3), handleBlur = (e) => {
    validateInput(currentValue), onBlur && onBlur(e);
  }, selectionRef = useRef({ start: 0, end: 0 }), handleSelectionChange = ({
    nativeEvent: { selection }
  }) => {
    selectionRef.current = selection;
  }, handleChange = useCallback(
    (text) => {
      const formattedValue = formatInputValue(text, format, getValue);
      if (setCurrentValue(formattedValue), setErrorMessage(""), setValidationState("unknown"), onValidationChange && onValidationChange("unknown"), onChangeText && onChangeText(formattedValue), debouncedValidateInput(text), formattedValue !== text) {
        const inputElement = inputRef.current;
        if (inputElement) {
          const currentTextLength = text.length, textLengthChange = formattedValue.length - currentTextLength;
          "selectionStart" in inputElement && handleCursorUpdateWeb(inputElement, textLengthChange);
        }
      }
    },
    [format, onChangeText, onValidationChange, getValue, debouncedValidateInput]
  );
  useEffect(() => {
    prefilledValue && (validateInputValue(
      handleValidationChange,
      prefilledValue,
      format,
      required,
      customErrorMessages
    ), setCurrentValue(formatInputValue(prefilledValue, format, getValue)));
  }, []);
  const prevFormat = useRef(format);
  return useEffect(() => {
    const revalidate = () => {
      validateInputValue(
        handleValidationChange,
        currentValue,
        format,
        required,
        customErrorMessages
      ), setCurrentValue(formatInputValue(currentValue, format, getValue));
    };
    format !== prevFormat.current && revalidate(), prevFormat.current = format;
  }, [format, handleValidationChange, currentValue, required, customErrorMessages, getValue]), /* @__PURE__ */ jsx(
    TextInputBase,
    {
      ...formatProps,
      ...props,
      ref: combinedInputRef,
      required,
      errorText: errorMessage || errorText,
      onChangeText: handleChange,
      value: currentValue,
      onBlur: handleBlur,
      invalid: validationState === "invalid",
      suggestion,
      onSuggestion: () => {
        suggestion && (setCurrentValue(suggestion), setSuggestion(void 0), validateInput(suggestion));
      },
      onSelectionChange: handleSelectionChange
    }
  );
}), handleCursorUpdateWeb = (inputElement, textLengthChange) => {
  const cursorPosition = inputElement.selectionStart;
  if (cursorPosition !== null) {
    const newCursorPosition = cursorPosition + textLengthChange;
    window.requestAnimationFrame(() => {
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  }
};
export {
  ValidatedTextInput
};
